import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {
  signIn,
  getCurrentUser,
  signOut,
  confirmSignIn,
} from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { JSX, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { login } from "@/store/authSlice";
import { useModal } from "@/hooks/useModal";
import OtpTab from "./OtpForm";
import { useAppDispatch } from "@/hooks/redux";
import * as Sentry from "@sentry/react";

enum LoginStage {
  USERNAME_PASSWORD,
  VERIFY_EMAIL,
  MFA,
  NEW_PASSWORD_REQUIRED,
}

const loginSchema = z.object({
  username: z.string().trim().min(1, "Username or email is required"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(5, "Password is not long enough"),
});

const otpSchema = z.object({
  code: z
    .string()
    .min(6, "OTP code must be 6 digits")
    .max(6, "OTP code must be 6 digits"),
});

const LoginTab = ({
  onLogin,
  defaultUsername,
  defaultPassword,
}: {
  onLogin?: () => void;
  defaultUsername?: string;
  defaultPassword?: string;
}) => {
  const dispatch = useAppDispatch();
  const { closeModal } = useModal();
  const { toast } = useToast();
  const [loginStage, setLoginStage] = useState(LoginStage.USERNAME_PASSWORD);
  const [otpDestination, setOtpDestination] = useState<string>("");
  const {
    register: registerLogin,
    handleSubmit: handleSubmitLogin,
    watch: watchLogin,
    setError: setErrorLogin,
    formState: {
      errors: errorsLogin,
      isValid: isValidLogin,
      isSubmitting: isSubmittingLogin,
    },
  } = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
    defaultValues: {
      username: defaultUsername || "",
      password: defaultPassword || "",
    },
  });
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");

  /**
   * Handle login form submission
   *
   * @param { z.infer<typeof loginSchema> } data
   */
  async function onLoginSubmit(data: z.infer<typeof loginSchema>) {
    try {
      await signOut();
    } catch (error) {
      Sentry.captureException(new Error("error signing out: " + error));
    }

    await signIn({
      username: data.username,
      password: data.password,
      options: {
        authFlowType: "USER_SRP_AUTH",
      },
    })
      .then((result) => {
        switch (result.nextStep.signInStep) {
          case "CONFIRM_SIGN_UP":
            setOtpDestination(result.nextStep.signInStep || "");
            setLoginStage(LoginStage.VERIFY_EMAIL);
            break;
          case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
            setLoginStage(LoginStage.NEW_PASSWORD_REQUIRED);
            break;
          case "DONE":
            return getCurrentUser().then(async (cognitoUser) => {
              await dispatch(login(cognitoUser));
              if (onLogin) {
                onLogin();
              }
              closeModal();
            });
            break;
          default:
            toast({
              title: "Login Failed",
              description: `Unsupported sign-in step: ${result.nextStep.signInStep}`,
            });
            setErrorLogin("root", {
              type: "manual",
              message: `Unsupported sign-in step: ${result.nextStep.signInStep}`,
            });
        }
      })
      .catch((error) => {
        console.error("SignIn error:");
        console.error(error);
        if (error.name === "UserLambdaValidationException") {
          if (error.message.includes("PreAuthentication failed with error ")) {
            let message = error.message
              .replace("PreAuthentication failed with error ", "")
              .replace(/\.$/, "")
              .trim();

            let parsedMessage: {
              message: string;
              destination?: string;
            } = message;
            try {
              parsedMessage = JSON.parse(message);
              setOtpDestination(parsedMessage.destination || "");
              toast({
                title: "Email Verification Required",
                description: parsedMessage.message,
              });
            } catch (e) {
              console.warn("Failed to parse error message as JSON:", e);
            }
          }
          setLoginStage(LoginStage.VERIFY_EMAIL);
        } else {
          toast({
            title: "Login Failed",
            description:
              error instanceof Error
                ? error.message
                : "An unknown error occurred during login.",
          });
          setErrorLogin("root", {
            type: "manual",
            message:
              error instanceof Error
                ? error.message
                : "An unknown error occurred during login.",
          });
        }
      });
  }

  let LoginContent: JSX.Element | null = null;

  switch (loginStage) {
    case LoginStage.USERNAME_PASSWORD:
      LoginContent = (
        <form onSubmit={handleSubmitLogin(onLoginSubmit)}>
          <TextField
            label="Email or Username"
            margin="normal"
            size="small"
            fullWidth
            variant="standard"
            autoComplete="username"
            slotProps={{
              input: { ...registerLogin("username") },
            }}
            error={!!errorsLogin.username}
            helperText={errorsLogin.username?.message?.toString() || " "}
          />
          <TextField
            label="Password"
            placeholder="Enter your password"
            type="password"
            margin="normal"
            size="small"
            fullWidth
            variant="standard"
            autoComplete="current-password"
            slotProps={{
              input: {
                ...registerLogin("password"),
              },
            }}
            error={!!errorsLogin.password}
            helperText={errorsLogin.password?.message?.toString() || " "}
          />
          <Button
            disabled={!isValidLogin || isSubmittingLogin}
            type="submit"
            variant="contained"
            sx={{ mt: 3 }}
            fullWidth
          >
            Login
          </Button>
        </form>
      );
      break;
    case LoginStage.VERIFY_EMAIL:
      LoginContent = (
        <OtpTab
          username={watchLogin("username")}
          destination={otpDestination}
          onSuccess={async () => {
            onLoginSubmit({
              username: watchLogin("username"),
              password: watchLogin("password"),
            }).catch((error) => {
              console.error("Error during post-OTP login:", error);
              setLoginStage(LoginStage.USERNAME_PASSWORD);
            });
          }}
        />
      );
      break;
    case LoginStage.MFA:
      LoginContent = <div>MFA Step - to be implemented</div>;
      break;
    case LoginStage.NEW_PASSWORD_REQUIRED:
      LoginContent = (
        <Box>
          <TextField
            label="New Password"
            placeholder="Enter your new password"
            type="password"
            margin="normal"
            size="small"
            fullWidth
            autoComplete="new-password"
            variant="standard"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            label="Confirm New Password"
            placeholder="Re-enter your new password"
            type="password"
            margin="normal"
            size="small"
            fullWidth
            autoComplete="new-password"
            variant="standard"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
          />
          <Button
            disabled={
              newPassword.length < 8 || newPassword !== confirmNewPassword
            }
            onClick={async () => {
              try {
                await confirmSignIn({
                  challengeResponse: newPassword,
                }).then(async () => {
                  return getCurrentUser().then(async (cognitoUser) => {
                    await dispatch(login(cognitoUser));
                    if (onLogin) {
                      onLogin();
                    }
                    closeModal();
                  });
                });
              } catch (error) {
                console.error("Error setting new password:", error);
                toast({
                  title: "Error",
                  description:
                    error instanceof Error
                      ? error.message
                      : "An unknown error occurred while setting new password.",
                });
              }
            }}
            variant="contained"
            sx={{ mt: 3 }}
            fullWidth
          >
            Set New Password
          </Button>
        </Box>
      );
      break;
    default:
      LoginContent = null;
  }

  return (
    <Box display={"flex"} sx={{ width: "100%" }}>
      <Box sx={{ mx: "auto" }}>{LoginContent}</Box>
    </Box>
  );
};

export default LoginTab;
